import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#fetchAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
  console.log('🛒 CartAddEvent received:', event.detail);
  
  if (this.hasAttribute('auto-open')) {
    this.showDialog();
    
    
  }

  this.#announceCartCount(event.detail.resource?.item_count);
};

  /**
   * Handles cart update events - refreshes the drawer content
   * @param {CustomEvent} event
   */
  #handleCartUpdate = async (event) => {
    console.log('🔄 Cart update event received, refreshing drawer content', event);
    await this.#refreshCartDrawer();
  };

  /**
   * Fetches and updates the cart drawer content
   */
  #refreshCartDrawer = async () => {
    try {
      // Abort any previous fetch request
      this.#fetchAbortController?.abort();
      this.#fetchAbortController = new AbortController();

      console.log('📡 Fetching updated cart content from:', window.location.pathname);

      // Get the current URL and add a timestamp to prevent caching
      const url = new URL(window.location.href);
      url.searchParams.append('_t', Date.now());

      // Fetch the current page to get the updated drawer content
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        },
        signal: this.#fetchAbortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      console.log('📄 Response received, parsing HTML...');

      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');

      // Find the dialog element in the new document
      const newDialog = newDoc.querySelector('cart-drawer-component')?.querySelector('[ref="dialog"]') ||
                        newDoc.querySelector('[data-testid="cart-drawer-dialog"]');

      // Find the cart drawer inner in the new document
      let newCartDrawerInner = newDoc.querySelector('[data-hydration-key="cart-drawer-inner"]');

      if (!newCartDrawerInner && newDialog) {
        newCartDrawerInner = newDialog.querySelector('[data-hydration-key="cart-drawer-inner"]');
      }

      // Find the current cart drawer inner in this component
      const dialog = this.refs.dialog;
      let currentCartDrawerInner = dialog?.querySelector('[data-hydration-key="cart-drawer-inner"]');

      if (!currentCartDrawerInner) {
        currentCartDrawerInner = this.querySelector('[data-hydration-key="cart-drawer-inner"]');
      }

      console.log('Found new drawer inner:', !!newCartDrawerInner);
      console.log('Found current drawer inner:', !!currentCartDrawerInner);

      if (newCartDrawerInner && currentCartDrawerInner) {
  console.log('✅ Updating drawer content...');
  
  // Sostituisci con il nuovo contenuto
  currentCartDrawerInner.innerHTML = newCartDrawerInner.innerHTML;

  this.#updateStickyState();

  console.log('✅ Drawer content updated successfully');
  
  // Reload dopo che il drawer si è aggiornato
  setTimeout(() => {
    console.log('🔄 Reloading page...');
  }, 50);
}
      else {
        console.warn('⚠️ Could not find drawer inner elements to update');
        console.log('newCartDrawerInner:', newCartDrawerInner);
        console.log('currentCartDrawerInner:', currentCartDrawerInner);
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ Error refreshing cart drawer:', error);
      }
    }


  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
